import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { 
  User, Edit, Globe, MessageSquare, Settings, 
  Mail, FileText, Inbox, ArrowLeft, Eye, Bot, CheckCircle, Tag, Database, Headset, CalendarDays, Zap, SlidersHorizontal
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIActorManage() {
  const { actorId } = useParams();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [actor, setActor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActorDetails();
    fetchUnreadCount();
  }, [actorId]);

  const fetchActorDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActor(response.data);
    } catch (error) {
      console.error("Failed to fetch actor details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/form-submissions/unread-counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data[actorId] || 0);
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  };

  const handleViewClick = () => {
    window.open(`/ai/actors/view/${actorId}`, '_blank');
  };

  const handleEditClick = () => {
    window.open(`/ai/actors/edit/${actorId}`, '_blank');
  };

  const handleWidgetSettingsClick = () => {
    window.open(`/ai/actors/${actorId}/widget-settings`, '_blank');
  };

  const handleAIConfigClick = () => {
    window.open(`/ai/actors/${actorId}/chat-config`, '_blank');
  };

  const handleFineTuningClick = () => {
    navigate(`/ai/actors/${actorId}/fine-tuning`);
  };

  const handleEmailConfigClick = () => {
    window.open(`/ai/actors/${actorId}/email-config`, '_blank');
  };

  const handlePublicProfileClick = () => {
    window.open(`/ai/actors/${actorId}/public-profile`, '_blank');
  };

  const handleInquiryFormClick = () => {
    window.open(`/ai/actors/${actorId}/inquiry-form`, '_blank');
  };

  const handleFormSubmissionsClick = () => {
    window.open(`/ai/actors/${actorId}/form-submissions`, '_blank');
  };

  const handleBackClick = () => {
    navigate('/ai/actors');
  };

  const handleModelsSettingsClick = () => {
    navigate(`/ai/actors/${actorId}/models-settings`);
  };

  const handleAutomationRulesClick = () => {
    navigate(`/ai/actors/${actorId}/automation-rules`);
  };

  const handleDraftingConditionsClick = () => {
    navigate(`/ai/actors/${actorId}/drafting-conditions`);
  };

  const handleEmailTagsClick = () => {
    navigate(`/ai/actors/${actorId}/email-tags`);
  };

  const handleOperationLogsClick = () => {
    navigate(`/ai/actors/${actorId}/operation-logs`);
  };

  const handleEmailKnowledgeBaseClick = () => {
    navigate(`/ai/actors/${actorId}/email-knowledge-base`);
  };

  const handleInboxClick = () => {
    navigate(`/ai/actors/${actorId}/emails`);
  };

  // Define categories with their actions
  const categories = [
    {
      id: "profile",
      icon: User,
      title: "Profile",
      description: "Manage actor profile and visibility",
      color: "border-t-blue-500",
      actions: [
        {
          id: "view",
          icon: Eye,
          label: "View Profile",
          description: "Preview your AI actor profile",
          handler: handleViewClick
        },
        {
          id: "edit",
          icon: Edit,
          label: "Edit Profile",
          description: "Update actor information",
          handler: handleEditClick
        },
        {
          id: "public-profile",
          icon: Globe,
          label: "Public Profile",
          description: "Configure visibility settings",
          handler: handlePublicProfileClick
        }
      ]
    },
    {
      id: "chat",
      icon: MessageSquare,
      title: "Chat",
      description: "Configure chat experience",
      color: "border-t-green-500",
      actions: [
        {
          id: "widget",
          icon: Settings,
          label: "Widget Settings",
          description: "Customize chat widget UI",
          handler: handleWidgetSettingsClick
        },
        {
          id: "ai-config",
          icon: Bot,
          label: "AI Configuration",
          description: "Configure AI model & behavior",
          handler: handleAIConfigClick
        },
        {
          id: "fine-tuning",
          icon: SlidersHorizontal,
          label: "Fine Tuning",
          description: "Tune content limits, link expiry & audio handling",
          handler: handleFineTuningClick
        }
      ]
    },
    {
      id: "email",
      icon: Mail,
      title: "Email",
      description: "Configure email account settings",
      color: "border-t-orange-500",
      actions: [
        {
          id: "inbox",
          icon: Inbox,
          label: "Inbox",
          description: "View and manage emails",
          handler: handleInboxClick
        },
        {
          id: "email-config",
          icon: Mail,
          label: "Email Configuration",
          description: "Configure SMTP & IMAP settings",
          handler: handleEmailConfigClick
        },
        {
          id: "models-settings",
          icon: Settings,
          label: "Models & Settings",
          description: "Configure AI models for email",
          handler: handleModelsSettingsClick
        },
        {
          id: "automation-rules",
          icon: CheckCircle,
          label: "Automation Rules",
          description: "Create email automation rules",
          handler: handleAutomationRulesClick
        },
        {
          id: "drafting-conditions",
          icon: FileText,
          label: "Drafting Conditions",
          description: "Custom rules for AI email drafts",
          handler: handleDraftingConditionsClick
        },
        {
          id: "email-tags",
          icon: Tag,
          label: "Email Tags",
          description: "View all email tags",
          handler: handleEmailTagsClick
        },
        {
          id: "operation-logs",
          icon: FileText,
          label: "Operation Logs",
          description: "View AI operation history",
          handler: handleOperationLogsClick
        },
        {
          id: "email-knowledge-base",
          icon: Database,
          label: "Email Knowledge Base",
          description: "RAG-powered semantic search",
          handler: handleEmailKnowledgeBaseClick
        }
      ]
    },
    {
      id: "whatsapp",
      icon: MessageSquare,
      title: "WhatsApp",
      description: "Manage WhatsApp conversations",
      color: "border-t-green-500",
      actions: [
        {
          id: "whatsapp-inbox",
          icon: MessageSquare,
          label: "WhatsApp Inbox",
          description: "View and manage WhatsApp conversations",
          handler: () => navigate(`/ai/actors/${actorId}/whatsapp`)
        },
        {
          id: "whatsapp-settings",
          icon: Settings,
          label: "WhatsApp Settings",
          description: "Configure WhatsApp integration",
          handler: () => navigate(`/ai/actors/${actorId}/whatsapp/settings`)
        }
      ]
    },
    {
      id: "form",
      icon: FileText,
      title: "Form",
      description: "Manage inquiry forms",
      color: "border-t-purple-500",
      actions: [
        {
          id: "form-builder",
          icon: FileText,
          label: "Inquiry Form Builder",
          description: "Create custom inquiry forms",
          handler: handleInquiryFormClick
        },
        {
          id: "form-submissions",
          icon: Inbox,
          label: "Form Submissions",
          description: "View and manage responses",
          handler: handleFormSubmissionsClick,
          badge: unreadCount > 0 ? unreadCount : null
        }
      ]
    },
    {
      id: "actor-functions",
      icon: Zap,
      title: "Actor Functions",
      description: "Support, Booking & integrations",
      color: "border-t-blue-500",
      actions: [
        ...(actor?.actor_functions?.includes('support') ? [{
          id: "zoho-support",
          icon: Headset,
          label: "Zoho Support",
          description: "Enabled — client identification & Zoho data",
          handler: handleEditClick,
          badge: "Active"
        }] : [{
          id: "zoho-support-off",
          icon: Headset,
          label: "Zoho Support",
          description: "Not enabled — edit profile to enable",
          handler: handleEditClick
        }]),
        ...(actor?.actor_functions?.includes('booking') ? [{
          id: "booking",
          icon: CalendarDays,
          label: "Booking",
          description: "Enabled — appointment scheduling active",
          handler: handleEditClick,
          badge: "Active"
        }] : [{
          id: "booking-off",
          icon: CalendarDays,
          label: "Booking",
          description: "Not enabled — edit profile to enable",
          handler: handleEditClick
        }]),
        {
          id: "edit-functions",
          icon: Settings,
          label: "Edit Actor Functions",
          description: "Enable/disable support, booking, web search",
          handler: handleEditClick
        }
      ]
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl mx-auto p-6 min-h-screen">
      {/* Header Section */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={handleBackClick}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to AI Actors
        </Button>
        
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-2">Manage AI Actor</h1>
          {actor && (
            <p className="text-muted-foreground text-lg">
              {actor.name}
              {actor.experience_level && ` • ${actor.experience_level}`}
            </p>
          )}
        </div>
      </div>

      {/* Vertical Layout with Category Cards */}
      <div className="flex flex-col gap-6 w-full">
        {categories.map((category) => {
          const CategoryIcon = category.icon;
          
          return (
            <Card key={category.id} className={`border-t-4 ${category.color}`}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CategoryIcon className="w-5 h-5" />
                  {category.title}
                </CardTitle>
                <CardDescription>{category.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {category.actions.map((action) => {
                    const ActionIcon = action.icon;
                    
                    return (
                      <Button
                        key={action.id}
                        variant="ghost"
                        onClick={action.handler}
                        className="w-full justify-start gap-3 h-auto py-3 px-4 hover:bg-accent hover:translate-x-1 transition-all"
                      >
                        <ActionIcon className="w-5 h-5 flex-shrink-0" />
                        <div className="flex-1 text-left">
                          <div className="font-medium flex items-center gap-2">
                            {action.label}
                            {action.badge && (
                              <Badge 
                                className="bg-red-500 text-white h-5 min-w-5 px-1.5"
                                variant="destructive"
                              >
                                {action.badge}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {action.description}
                          </div>
                        </div>
                      </Button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
